import { ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import React, { useState } from 'react';

import api from '@/app/services/axios.service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { OnboardingData, OnboardingStep } from '../index';

interface CompleteProfileProps {
  onNext: (data: Partial<OnboardingData>, nextStep?: OnboardingStep) => void;
  data: OnboardingData;
}

export default function CompleteProfile({ onNext, data }: CompleteProfileProps) {
  const [firstName, setFirstName] = useState(data.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(data.name?.split(' ').slice(1).join(' ') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validatePassword = (pass: string) => {
    const hasSpecialChar = /[!@#$%^&*]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasNumber = /[0-9]/.test(pass);
    const hasMinLength = pass.length >= 8;

    if (!hasSpecialChar) return 'Password must contain at least one special character (!@#$%^&*)';
    if (!hasLowerCase) return 'Password must contain at least one lowercase letter';
    if (!hasUpperCase) return 'Password must contain at least one uppercase letter';
    if (!hasNumber) return 'Password must contain at least one number';
    if (!hasMinLength) return 'Password must be at least 8 characters long';
    return null;
  };

  const handleSubmit = async () => {
    const errors: string[] = [];
    if (!firstName.trim()) errors.push('First name is required');
    if (!lastName.trim()) errors.push('Last name is required');
    if (!password) errors.push('Password is required');
    if (!confirmPassword) errors.push('Confirm password is required');
    if (password && confirmPassword && password !== confirmPassword)
      errors.push('Passwords do not match');
    const passwordError = password ? validatePassword(password) : null;
    if (passwordError) errors.push(passwordError);
    if (errors.length > 0) {
      setError(errors);
      return;
    }
    setError(null);
    setIsLoading(true);
    try {
      const response = await api.post('/users', {
        name: firstName,
        surname: lastName,
        email: data.email,
        password: password,
        client: data.company,
        inviteCode: data['inviteCode'],
      });
      if (response.status < 200 || response.status >= 300) {
        const errorData = response.data;
        throw new Error(errorData.message || 'Failed to create user');
      }

      setIsLoading(false);
      setIsSuccess(true);
      setTimeout(() => {
        onNext({
          name: `${firstName} ${lastName}`.trim(),
        });
      }, 1500);
    } catch (err: any) {
      let msg = err instanceof Error ? err.message : 'Failed to create user';
      if (msg === 'Request failed with status code 500') {
        msg = 'Something went wrong on our end. Please try again later.';
      }
      setError(msg);
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onNext({}, 'signup');
  };

  return (
    <div className="flex flex-col items-center justify-center w-full max-w-md mx-auto space-y-8">
      {isSuccess ? (
        <div className="w-full pt-4 space-y-2 text-center">
          <img
            src="/icons/brand/revi/monogram-black.svg"
            alt="revi-logo"
            className="mx-auto mb-8 size-8"
          />
          <h2 className="text-xl font-semibold">Profile Created Successfully!</h2>
          <p className="text-sm text-muted-foreground">You will be redirected shortly...</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center gap-2">
            <img
              src="/icons/brand/revi/monogram-black.svg"
              alt="revi-logo"
              className="mb-4 size-8"
            />
            <h1 className="text-3xl text-center">Complete your profile</h1>
            <p className="text-sm font-normal text-muted-foreground">{data.company || ''}</p>
          </div>

          <div className="w-full pt-4 space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">Name</Label>
                <div className="flex gap-2">
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="First name"
                    className="font-normal"
                    required
                  />
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Last name"
                    className="font-normal"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="font-normal"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute -translate-y-1/2 cursor-pointer right-3 top-1/2 text-muted-foreground"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Password must contain at least 8 characters, including uppercase, lowercase,
                  numbers, and special characters (!@#$%^&*)
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="font-normal"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute -translate-y-1/2 cursor-pointer right-3 top-1/2 text-muted-foreground"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>
            {error && (
              <Alert variant="destructive" className="p-0 pl-2 -mt-2 border-none">
                <AlertDescription className="p-0 text-xs font-normal">
                  {Array.isArray(error) ? error.map((msg, i) => <div key={i}>{msg}</div>) : error}
                </AlertDescription>
              </Alert>
            )}
            <Button
              onClick={handleSubmit}
              className="w-full"
              variant="default"
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Continue'}
            </Button>

            <Button
              variant="ghost"
              className="flex items-center gap-2 -mt-2 font-normal text-muted-foreground justify-self-center"
              onClick={handleBack}
              type="button"
              disabled={isLoading}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
